import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  DeviceLink,
  EmptyState,
  Input,
  RangePicker,
  Spinner,
} from '@/components';
import type { BehaviorEvent, BehaviorTimelineResponse, Range } from '@/lib/admin-types';
import {
  activeDayWindow,
  formatPacificDayLabel,
  kindMeta,
  pacificDayKey,
  pacificMinutesOfDay,
  pctInWindow,
  stackByProximity,
  windowTickLabels,
} from '@/lib/behavior-viz';
import { formatAdminTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type TimelineSearch = {
  range: Range;
  installation_id: string;
};

type Lane = {
  installation_id: string;
  nickname: string | null;
  events: BehaviorEvent[];
};

type HoverState = {
  events: BehaviorEvent[];
  x: number;
  y: number;
};

function DayLaneChart({
  dayKey,
  events,
}: {
  dayKey: string;
  events: BehaviorEvent[];
}) {
  const lanes = useMemo(() => {
    const map = new Map<string, Lane>();
    for (const e of events) {
      const cur = map.get(e.installation_id);
      if (cur) cur.events.push(e);
      else {
        map.set(e.installation_id, {
          installation_id: e.installation_id,
          nickname: e.nickname,
          events: [e],
        });
      }
    }
    return [...map.values()]
      .map((l) => ({
        ...l,
        events: [...l.events].sort(
          (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
        ),
      }))
      .sort((a, b) => b.events.length - a.events.length)
      .slice(0, 24);
  }, [events]);

  const dayWindow = useMemo(() => {
    const mins = events.map((e) => pacificMinutesOfDay(e.at));
    return activeDayWindow(mins, { padMin: 40, minSpanMin: 150 });
  }, [events]);

  const ticks = windowTickLabels(dayWindow, 5);
  const proximityMin = Math.max(4, (dayWindow.endMin - dayWindow.startMin) * 0.02);

  const [hover, setHover] = useState<HoverState | null>(null);

  if (lanes.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {formatPacificDayLabel(dayKey)}
        </h3>
        <p className="text-xs text-gray-500">
          {events.length} events · {lanes.length} device lane
          {lanes.length === 1 ? '' : 's'}
          {lanes.length === 24 ? ' (top 24)' : ''} · zoomed to active hours
        </p>
      </div>

      <div className="relative overflow-x-auto rounded-2xl border border-gray-100 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="mb-2 flex justify-between pl-36 text-[10px] tabular-nums text-gray-400">
          {ticks.map((t) => (
            <span key={`${dayKey}-${t.min}`}>{t.label}</span>
          ))}
        </div>
        <div className="space-y-2">
          {lanes.map((lane) => {
            const stacked = stackByProximity(lane.events, (e) => pacificMinutesOfDay(e.at), {
              proximityMin,
              maxStack: 5,
            });
            const maxStack = Math.max(0, ...stacked.map((s) => s.stack));
            const laneHeight = 22 + maxStack * 10;

            // Cluster for hover tooltips: group events that share nearly the same x.
            const clusters = new Map<number, BehaviorEvent[]>();
            for (const { item, stack } of stacked) {
              const bucket = Math.round(pacificMinutesOfDay(item.at) / proximityMin);
              const list = clusters.get(bucket) ?? [];
              list.push(item);
              clusters.set(bucket, list);
              void stack;
            }

            return (
              <div key={lane.installation_id} className="flex items-center gap-3">
                <div className="w-36 shrink-0 truncate">
                  <DeviceLink
                    installationId={lane.installation_id}
                    nickname={lane.nickname}
                    className="text-xs font-medium"
                  />
                </div>
                <div
                  className="relative min-w-[480px] flex-1 rounded-lg bg-white/70 ring-1 ring-inset ring-gray-200/70"
                  style={{ height: laneHeight }}
                >
                  {[25, 50, 75].map((pct) => (
                    <div
                      key={pct}
                      className="pointer-events-none absolute top-0 bottom-0 w-px bg-gray-100"
                      style={{ left: `${pct}%` }}
                    />
                  ))}
                  {stacked.map(({ item: e, stack }, i) => {
                    const mins = pacificMinutesOfDay(e.at);
                    const left = pctInWindow(mins, dayWindow);
                    const m = kindMeta(e.kind);
                    const bucket = Math.round(mins / proximityMin);
                    const group = clusters.get(bucket) ?? [e];
                    const top = 4 + stack * 10;
                    return (
                      <button
                        key={`${lane.installation_id}-${e.at}-${i}`}
                        type="button"
                        className={clsx(
                          'absolute h-2.5 w-2.5 -translate-x-1/2 rounded-full shadow-sm ring-2 ring-white transition hover:scale-125',
                          m.bar
                        )}
                        style={{ left: `${left}%`, top }}
                        title={`${m.label}: ${e.summary}`}
                        onMouseEnter={(ev) => {
                          const rect = (ev.target as HTMLElement).getBoundingClientRect();
                          setHover({
                            events: group,
                            x: rect.left + rect.width / 2,
                            y: rect.top,
                          });
                        }}
                        onMouseLeave={() => setHover(null)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {hover && (
          <div
            className="pointer-events-none fixed z-50 max-w-xs rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
            style={{
              left: Math.min(hover.x, window.innerWidth - 220),
              top: hover.y - 8,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {hover.events.length > 1 && (
              <p className="mb-1 font-semibold text-gray-700">
                {hover.events.length} events near this time
              </p>
            )}
            <ul className="space-y-1.5">
              {hover.events.slice(0, 6).map((e, i) => (
                <li key={`${e.at}-${i}`}>
                  <p className={clsx('font-semibold', kindMeta(e.kind).color)}>
                    {kindMeta(e.kind).label}
                  </p>
                  <p className="text-gray-800">{e.summary}</p>
                  <p className="tabular-nums text-gray-500">{formatAdminTime(e.at)}</p>
                </li>
              ))}
              {hover.events.length > 6 && (
                <li className="text-gray-500">+{hover.events.length - 6} more</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function TimelinePage() {
  const { range, installation_id } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/analytics/timeline' });
  const [draftId, setDraftId] = useState(installation_id);

  const q = useQuery({
    queryKey: ['admin', 'behavior', 'timeline', range, installation_id],
    queryFn: () => {
      const p = new URLSearchParams({ range, limit: '1200' });
      if (installation_id.trim()) p.set('installation_id', installation_id.trim());
      return apiJson<BehaviorTimelineResponse>(
        `/v1/admin/analytics/behavior/timeline?${p.toString()}`
      );
    },
    refetchInterval: 30_000,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, BehaviorEvent[]>();
    for (const e of q.data?.items ?? []) {
      const key = pacificDayKey(e.at);
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [q.data?.items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Day-lane timeline</h2>
          <p className="mt-0.5 text-sm text-gray-600">
            One row per device; nearby events stack vertically instead of overlapping.
          </p>
        </div>
        <RangePicker
          value={range === '24h' ? '7d' : range}
          options={['7d', '30d', '90d']}
          onChange={(r) =>
            void navigate({ search: (prev) => ({ ...prev, range: r }) })
          }
        />
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({
            search: (prev) => ({ ...prev, installation_id: draftId.trim() }),
          });
        }}
      >
        <div className="min-w-[240px] flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Focus one device (optional)
          </label>
          <Input
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
            placeholder="Paste installation UUID"
          />
        </div>
        <button
          type="submit"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white"
        >
          Apply
        </button>
        {installation_id && (
          <button
            type="button"
            className="rounded-xl px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            onClick={() => {
              setDraftId('');
              void navigate({ search: (prev) => ({ ...prev, installation_id: '' }) });
            }}
          >
            Clear
          </button>
        )}
      </form>

      <div className="flex flex-wrap gap-2">
        {(
          [
            'run',
            'flashcard_set_completion',
            'storybook_completion',
            'alphabet_sound_listen',
            'resource_tap',
            'flashcard_view',
          ] as const
        ).map((k) => {
          const m = kindMeta(k);
          return (
            <span
              key={k}
              className="inline-flex items-center gap-1.5 text-[11px] text-gray-600"
            >
              <span className={clsx('h-2.5 w-2.5 rounded-full', m.bar)} />
              {m.label}
            </span>
          );
        })}
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : q.isError ? (
        <p className="text-sm text-red-600">
          {(q.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void q.refetch()}>
            Retry
          </button>
        </p>
      ) : byDay.length === 0 ? (
        <EmptyState title="No timeline events in this range" />
      ) : (
        <div className="space-y-8">
          {byDay.map(([day, dayEvents]) => (
            <DayLaneChart key={day} dayKey={day} events={dayEvents} />
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/analytics/timeline')({
  validateSearch: (raw: Record<string, unknown>): TimelineSearch => {
    const r = raw.range;
    const range: Range =
      r === '30d' || r === '90d' || r === '7d' || r === '24h' ? (r === '24h' ? '7d' : r) : '7d';
    return {
      range,
      installation_id: typeof raw.installation_id === 'string' ? raw.installation_id : '',
    };
  },
  component: TimelinePage,
});
